import { SITE } from "~/constants";

/**
 * Sourei GTM — port of the global analytics section from the original site.
 *
 * Origin history: the CMS Site block used to declare the vendored
 * `sourei/sections/Analytics/Sourei.tsx` global (hostname + gtmId + loading
 * "eager"), which loaded `/gtm.js?id=GTM-5PCJKR3` and bridged
 * `window.DECO.events` into `dataLayer`. On 14/09/2026 upstream replaced it
 * with a local section, `sections/Analytics/SoureiCookieShield.tsx`
 * (`97d8e37be`), and the Site block now points at that instead.
 *
 * Either way the framework skips analytics sections during CMS resolution
 * (SKIP_RESOLVE_TYPES in @decocms/blocks), so this is wired in code. The
 * snippet below é declarado em `head().scripts` no `__root.tsx` (TanStack não
 * hasteia `<script>` do body para o `<head>`). Desde #246 ele voltou a carregar
 * eager, fora do gate de `~/sdk/deferThirdParty` — o gate hoje só adia o
 * Konfidency. This component carries the <noscript> iframe.
 *
 * The container is what injects the third-party floating widgets on
 * production — notably the BonifiQ/MPoints loyalty button (custom HTML
 * tag loading bq-scripts.s3.amazonaws.com/…/bqloader.js), plus Clarity,
 * Facebook pixel, btg360, Mailbiz and the Wake onsite plugin (DECO-5422).
 */

export const SOUREI_HOSTNAME = "https://soureicdn.miess.com.br";
export const SOUREI_GTM_ID = "GTM-5PCJKR3";

/**
 * Three tags inside the GTM-5PCJKR3 container (btg360, Allin and the
 * enhanced-ecommerce e-mail enrichment) fetch the shopper's VTEX session with
 * a hard-coded absolute origin:
 *
 *   fetch("https://www.miess.com.br/api/sessions?items=profile.email")
 *
 * On production that is same-origin, so the request carries cookies and
 * resolves the logged-in e-mail/phone. On any other host (the workers.dev
 * candidate, previews, localhost) it is cross-origin and VTEX answers without
 * `Access-Control-Allow-Origin`, so the browser blocks it — the tags fall into
 * their `catch` ("Fetch falhou: Failed to fetch") and push anonymous events,
 * silently breaking e-mail-based personalization (DECO-5824).
 *
 * The container is third-party content we can't edit, so rewrite those calls
 * to the same-origin `/api/sessions`, which the worker already proxies to VTEX
 * (`shouldProxyToVtex` in `src/worker-entry.ts`) — cookies included. No-op once
 * the site runs on www.miess.com.br, where the URL is already same-origin.
 */
const PROD_SESSIONS_URL = `https://${SITE.DOMAIN}/api/sessions`;

export const SESSION_FETCH_SHIM_SCRIPT = `(function () {
  if (!window.fetch || window.location.host === "${SITE.DOMAIN}") return;
  if (window.__miessSessionFetchPatched) return;
  window.__miessSessionFetchPatched = true;

  var PROD = "${PROD_SESSIONS_URL}";
  var original = window.fetch;

  window.fetch = function (input, init) {
    try {
      var url = typeof input === "string" ? input
        : input instanceof URL ? input.href
        : input && input.url;
      if (typeof url === "string" && url.indexOf(PROD) === 0) {
        var samePath = "/api/sessions" + url.slice(PROD.length);
        return original.call(
          window,
          typeof input === "string" || input instanceof URL
            ? samePath
            : new Request(samePath, input),
          init
        );
      }
    } catch (e) {}
    return original.apply(window, arguments);
  };
})();`;

/**
 * Ponte `window.DECO.events` → `window.dataLayer`.
 *
 * A seção antiga do Sourei carregava o `gtm.js` E carregava esta ponte. Em
 * 14/09/2026 a origem trocou a seção pelo cookie shield (`97d8e37be`) e a
 * ponte foi junto, na premissa de que "o novo container" resolveria os eventos
 * de ecommerce. Não resolveu: o `AnalyticsRegistrar` continua despachando em
 * `window.DECO.events`, ninguém assina, e `view_item`/`add_to_cart`/`purchase`
 * nunca chegam ao GA4. É o mesmo snippet que o pacote já embute em
 * `@decocms/apps-website/components/Analytics.tsx` — inclusive o desvio do
 * evento `deco`, que vai em `deco:` e não em `ecommerce:`, senão o objeto
 * `ecommerce` do GA4 é contaminado por parâmetros que não são de ecommerce.
 *
 * ORDEM — o fallback em `DOMContentLoaded` NÃO é decorativo. Quem define
 * `window.DECO.events` é o `<ScriptOnce>` do `DecoRootLayout`, no topo do
 * `<body>`, e a posição dos `head().scripts` em relação a ele MUDA conforme o
 * build: medido em `vite dev` (15/09/2026) este snippet saiu no byte 8261,
 * dentro do `<head>`, com o bootstrap só em 11017 — o `subscribe()` síncrono
 * encontra `window.DECO` indefinido e retorna sem erro. O pacote documenta a
 * mesma armadilha para o collector dele (`DecoRootLayout.tsx`): no `<head>`, a
 * assinatura simplesmente não acontece e nada é logado.
 *
 * Assinar tarde não perde evento: o `subscribe` do bootstrap reproduz a fila
 * `_q` de eventos já despachados para cada novo subscriber.
 *
 * `search` (#287): o GA4 lê `search_term` como parâmetro do evento, na raiz,
 * não dentro de `ecommerce`. A ponte genérica mandava `ecommerce.search_term`
 * (igual à loja Fresh). Agora vai na raiz e a cópia em `ecommerce` fica para
 * não quebrar leitor externo. No container GTM-5PCJKR3 (lido em 27/10/2026)
 * nenhuma variável lê `ecommerce.search_term`; a tag GA4 `search` de lá nem
 * escuta `event: "search"`: dispara em `view_item_list`/`view_search_results`
 * com `s?q=` na URL e tira o termo do `?q=`.
 */
export const DECO_EVENTS_BRIDGE_SCRIPT = `(function(){
  window.dataLayer=window.dataLayer||[];
  var subscribed=false;
  function subscribe(){
    if(subscribed)return;
    var ev=window.DECO&&window.DECO.events;
    if(!ev||typeof ev.subscribe!=='function')return;
    subscribed=true;
    ev.subscribe(function(event){
      if(!event||!event.name)return;
      if(event.name==='deco'){
        window.dataLayer.push({event:event.name,deco:event.params});
        return;
      }
      window.dataLayer.push({ecommerce:null});
      if(event.name==='search'){
        var p=event.params||{};
        window.dataLayer.push({event:'search',search_term:p.search_term,ecommerce:p});
        return;
      }
      window.dataLayer.push({event:event.name,ecommerce:event.params});
    });
  }
  subscribe();
  document.addEventListener('DOMContentLoaded',subscribe,{once:true});
})();`;

/**
 * Cookie-shield loader, ported verbatim from upstream's
 * `sections/Analytics/SoureiCookieShield.tsx` (`97d8e37be`, 14/09/2026).
 *
 * It replaces `/gtm.js?id=GTM-5PCJKR3`: the container is now implicit in the
 * per-tenant token in the query, and the first-party `_wascer` identifier is
 * mirrored from localStorage into a 2-year cookie and appended as `&bi=`.
 *
 * Kept byte-for-byte as the vendor delivered it — it is third-party analytics
 * code and parity is the acceptance criterion. Note that it creates its own
 * `<script async>`, so this inline snippet does not block the parser (the
 * reason `__root.tsx` had to force `async` on the old `gtm.js` tag, #165).
 *
 * WHAT THIS DROPS: the previous section also bridged `window.DECO.events` into
 * `dataLayer` (item_group_id merge, price = price + discount, 1-based index).
 * Upstream removed that bridge deliberately — their commit message flags it:
 * e-commerce events (view_item, add_to_cart, purchase) now depend entirely on
 * the new container. Medido no candidato, não dependem: sem subscriber, nada
 * chega ao GA4. O shield fica byte-a-byte como a origem entregou e a ponte
 * voltou SEPARADA, em `DECO_EVENTS_BRIDGE_SCRIPT` acima — o que preserva a
 * paridade do snippet do fornecedor.
 */
export const SOUREI_COOKIE_SHIELD_SCRIPT = `!function(){"use strict";
var w=window,d=document,n="dataLayer",
    o="https://soureicdn.miess.com.br",
    i="miess",
    c="7=FksCBx4kDUdJUSo9KF5GPSovOCFeSxg",
    K="_wascer",A=63072000;
function gc(e){for(var p=d.cookie.split(";"),j=0;j<p.length;j++){var s=p[j].split("=");if(s[0].trim()===e)return s.slice(1).join("=")}}
function gl(e){try{return localStorage.getItem(e)}catch(x){}}
var ls=gl(K);
if(ls&&!gc(K)){d.cookie=K+"="+ls+"; Max-Age="+A+"; Path=/; SameSite=Lax"}
var a=gc(K)||ls,bi=a?"&bi="+encodeURIComponent(a):"";
w[n]=w[n]||[];w[n].push({"gtm.start":new Date().getTime(),event:"gtm.js"});
var f=d.getElementsByTagName("script")[0],s=d.createElement("script");
s.async=!0;s.src=o+"/"+i+".js?"+c+bi;
null!=f.parentNode&&f.parentNode.insertBefore(s,f);
}();`;

declare global {
  interface Window {
    __miessSessionFetchPatched?: boolean;
  }
}

export default function Sourei() {
  return (
    <>
      <noscript>
        <iframe
          title="Google Tag Manager"
          src={`${SOUREI_HOSTNAME}/ns.html?id=${SOUREI_GTM_ID}`}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        />
      </noscript>
    </>
  );
}
